import { useState, useRef, useCallback } from 'react';
import Icon from '@/components/shared/Icons';
import dynamic from 'next/dynamic';
import { Button } from '@/components/shared/button/Button';
import type { AIStrings } from '@/utils/filters/options/getFilterOptions';
import useAnalytics from '@/hooks/useAnalytics';
import { SimpleClickFact } from '@/analytics/models/cventAnalytics/SimpleClickFact';
import { getSimpleClickFact } from '@/analytics/helpers/cventAnalytics/getSimpleClickFact';
import useMap from '@/hooks/useMap';
import { usePathname, useSearchParams } from 'next/navigation';
import type { Filter } from '@cvent/venue-search-shared/types';
import { prepareUrl } from '@/utils/urlHelper';
import { useRouter } from '@/lib/router-events/patch-router/router';
import { AI_MODE_COOKIE_NAME } from '@/constants/cookieConstants';

const Switch = dynamic(() => import('@cvent/carina/components/Switch'), { ssr: false });
const Popper = dynamic(() => import('@/components/shared/popper/Popper'), { ssr: false });

type Props = {
  aiMode: boolean;
  strings: AIStrings;
  filters: Filter;
  cookieDomain: string;
};

export default function SearchToggle({ aiMode, strings, filters, cookieDomain }: Props): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const analytics = useAnalytics();
  const { mapView } = useMap();
  const path = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  const params = new URLSearchParams(searchParams === null ? undefined : searchParams);

  const setOrUpdateAiModeCookie = useCallback(
    (value: boolean): void => {
      if (typeof document !== 'undefined') {
        document.cookie = `${AI_MODE_COOKIE_NAME}=${value};path=/;domain=${cookieDomain};`;
      }
    },
    [cookieDomain]
  );

  const triggerSimpleClickFact = useCallback(
    (value: boolean) => {
      const simpleClickFact: SimpleClickFact = getSimpleClickFact(
        'SEARCH_2.0',
        value ? 'AI search toggle ON' : 'AI search toggle OFF',
        'null',
        'null',
        mapView,
        path.split('/')?.pop() ?? 'null',
        'null',
        'null',
        JSON.stringify(filters),
        'null',
        'null',
        'null',
        'search_results'
      );
      analytics.reportFact<SimpleClickFact>(simpleClickFact);
    },
    [mapView, path, filters, analytics]
  );

  return (
    <>
      <Button
        className={aiMode ? 'm-2' : 'mr-3'}
        ref={triggerRef}
        icon={<Icon id="more-vertical" className={`${aiMode ? 'h-5 w-5' : 'h-4 w-4'}`} />}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        size="s"
        appearance="ghost"
        aria-label={strings.aiIconAria}
        testID="ai-search-toggle-button"
      />
      {isOpen && (
        <Popper
          triggerRef={triggerRef}
          isOpen={isOpen}
          placement="bottom-end"
          preventOverflow
          hasShadow
          onDismiss={() => setIsOpen(false)}
        >
          <div className="rounded-sm bg-background p-4 font-bold text-neutral-60">
            {/* first row */}
            <div className="flex flex-row items-center justify-between gap-2">
              <div className="flex flex-row items-center justify-between gap-2">
                <Icon id="magic-wand" className="h-4 w-4" />
                <span>{strings.aiTitle}</span>
              </div>
              <Switch
                testID="ai-search-toggle-switch"
                onUpdate={value => {
                  triggerSimpleClickFact(value);
                  setOrUpdateAiModeCookie(value);
                  router.push(prepareUrl(path, params, ...[{ key: 'ai', value: Boolean(value).toString() }]));
                }}
                value={aiMode}
                aria-label={strings.aiToggleAria}
              />
            </div>

            {/* pendo thing */}
            {aiMode && (
              <>
                <div className="my-3 h-px w-full bg-neutral-40" />
                <div className="flex flex-col items-start">
                  <span className="text-b/xs uppercase">{strings.aiPendoTitle}</span>
                  <Button
                    variant="menu"
                    size="s"
                    className="-ml-2 mt-2 w-full justify-start pl-2 pr-4"
                    text={strings.aiPositiveText}
                    aria-label={strings.aiPositiveText}
                    appearance="ghost"
                    icon={<Icon id="thumbs-up" className="h-4 w-4" />}
                    testID="ai-search-positive-feedback"
                  />
                  <Button
                    variant="menu"
                    size="s"
                    className="-ml-2 pl-2 pr-4"
                    text={strings.aiNegativeText}
                    aria-label={strings.aiNegativeText}
                    appearance="ghost"
                    icon={<Icon id="thumbs-up" className="h-4 w-4 rotate-180" />}
                    testID="ai-search-negative-feedback"
                  />
                </div>
              </>
            )}
          </div>
        </Popper>
      )}
    </>
  );
}
